import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSiteSettings } from '../context/SiteSettingsContext';

// Canonical site URL. Configurable via REACT_APP_CANONICAL_SITE_URL so we
// don't have to redeploy if the canonical hostname ever moves — set this
// to your production domain once you have one; search engines should
// index a single canonical hostname even if the site is reachable on
// several (bare domain, www, alternate domain, etc.). Falls back to the
// current origin when unset, which works for local dev and any fresh
// deploy that hasn't configured a canonical domain yet.
const CANONICAL_SITE_URL = (() => {
  if (process.env.REACT_APP_CANONICAL_SITE_URL) {
    return process.env.REACT_APP_CANONICAL_SITE_URL.replace(/\/$/, '');
  }
  return typeof window !== 'undefined'
    ? window.location.origin.replace(/\/$/, '')
    : '';
})();

// Query params that are allowed to remain in a canonical URL. The language
// and the category define genuinely distinct, indexable pages (a category
// is /products?category=slug, matching the server's canonical). Everything else — sort order,
// pagination, category/tag/age filters, free-text search, view mode —
// produces near-duplicate content for the same logical page. If those
// params survive into the canonical, every filter/sort/page permutation
// self-canonicalises as a separate URL, which is exactly what triggers
// Google Search Console's "Duplicate without user-selected canonical".
// Stripping them consolidates all permutations onto one clean canonical
// (e.g. /products?category=cars&sort=price-low&page=2 → /products),
// matching the clean URLs already emitted in sitemap.xml.
const CANONICAL_KEEP_PARAMS = new Set(['lang', 'category']);

export const toCanonicalUrl = (rawUrl) => {
  if (!rawUrl) return CANONICAL_SITE_URL;
  if (!CANONICAL_SITE_URL) return rawUrl;
  try {
    const parsed = new URL(rawUrl, CANONICAL_SITE_URL);
    const kept = new URLSearchParams();
    for (const [key, value] of parsed.searchParams) {
      if (CANONICAL_KEEP_PARAMS.has(key)) kept.set(key, value);
    }
    const qs = kept.toString();
    // Note: the URL fragment (#…) is intentionally dropped — fragments
    // are never separate documents to a crawler.
    return `${CANONICAL_SITE_URL}${parsed.pathname}${qs ? `?${qs}` : ''}`;
  } catch {
    return rawUrl;
  }
};

/**
 * Page title, description and language while shoppers move around the app.
 *
 * Everything crawlers and link previews read — canonical, hreflang, Open
 * Graph, Twitter cards, JSON-LD — is written by the server for each URL
 * (src/Service/Seo/SeoPageRenderer.php). Repeating it here gave Google two
 * canonicals and two Product blocks per page, so the app no longer does.
 * The server marks its description with data-rh, so the one below replaces
 * it instead of adding a second. Other props are accepted and ignored so
 * existing pages don't need to change.
 */
const SEO = ({ title, description, noIndex = false }) => {
  const { storeName } = useSiteSettings();
  const { i18n } = useTranslation();
  const htmlLang = (i18n.language || 'en').split('-')[0];
  const fullTitle = title ? `${title} | ${storeName}` : storeName;

  return (
    <Helmet>
      <html lang={htmlLang} />
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
    </Helmet>
  );
};

export default SEO;
